import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    const navLinks = [
        { label: 'Home', href: '/' },
        { label: 'About', href: '/about' },
        { label: 'Find Teachers', href: '/find-teachers' },
        { label: 'Contact', href: '/contact' },
    ];

    const routeGroups = useMemo(
        () => ({
            '/': new Set(['/']),
            '/about': new Set(['/about']),
            '/find-teachers': new Set(['/find-teachers']),
            '/contact': new Set(['/contact']),
        }),
        []
    );

    const isActive = (path: string) => {
        if (path === '/find-teachers' && location.pathname.startsWith('/teacher/')) {
            return true;
        }

        return routeGroups[path as keyof typeof routeGroups]?.has(location.pathname) ?? false;
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileMenuOpen]);

    return (
        <>
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-20">
                    {/* Logo */}
                    <div className="flex items-center gap-12">
                        <Link className="flex items-center gap-2 group" to="/">
                            <div className="bg-navy p-1.5 rounded-lg transition-transform group-hover:scale-110">
                                <span className="material-symbols-outlined text-white block">school</span>
                            </div>
                            <span className="text-xl font-extrabold text-navy tracking-tight">LankaTutor</span>
                        </Link>

                        {/* Desktop Links */}
                        <div className="hidden lg:flex items-center gap-8">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className={`relative text-[15px] font-semibold transition-colors hover:text-primary ${isActive(link.href) ? 'text-primary' : 'text-navy/70'
                                         }`}
                                >
                                    {link.label}
                                    {isActive(link.href) && (
                                        <span className="absolute -bottom-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-primary" />
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Auth Buttons */}
                    <div className="hidden lg:flex items-center gap-3">
                        <Link
                            to="/signup"
                            className="bg-navy text-white px-4 py-2 rounded-full text-[13px] font-bold hover:bg-slate-800 transition-all shadow-lg shadow-navy/10"
                        >
                            Teacher Sign Up
                        </Link>
                        <Link
                            to="/student-signup"
                            className="bg-primary text-white px-4 py-2 rounded-full text-[13px] font-bold hover:bg-emerald-600 transition-all shadow-lg shadow-primary/20"
                        >
                            Student Sign Up
                        </Link>
                    </div>

                    {/* Mobile Hamburger Overlay */}
                    <div className="lg:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-expanded={isMobileMenuOpen}
                            aria-label="Toggle navigation menu"
                            className="flex h-11 w-11 flex-col items-center justify-center gap-1.5 rounded-2xl border border-slate-200/80 bg-white/90 text-navy shadow-lg shadow-slate-200/50 transition-transform duration-200 hover:scale-[1.02]"
                        >
                            <span className={`h-0.5 w-5 rounded-full bg-slate-800 transition-transform duration-300 ${isMobileMenuOpen ? 'translate-y-2 rotate-45' : ''}`} />
                            <span className={`h-0.5 w-5 rounded-full bg-slate-800 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                            <span className={`h-0.5 w-5 rounded-full bg-slate-800 transition-transform duration-300 ${isMobileMenuOpen ? '-translate-y-2 -rotate-45' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>
        </nav>
        {isMobileMenuOpen && (
            <>
                <div
                    className="fixed inset-0 z-40 bg-slate-950/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
                <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pt-20 transition-transform duration-300 lg:hidden translate-y-0">
                    <div className="mx-auto max-w-lg rounded-[2rem] border border-white/70 bg-white/95 p-4 shadow-[0_-24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                        <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-slate-200" />
                        <div className="mb-4">
                            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary/80">Explore LankaTutor</p>
                            <p className="mt-1 text-sm text-slate-500">Quick access to the main public pages and signup flows.</p>
                        </div>
                        <div className="space-y-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`flex items-center justify-between rounded-2xl px-4 py-3 text-base font-semibold transition-all ${isActive(link.href)
                                        ? 'bg-gradient-to-r from-indigo-50 to-blue-50 text-primary shadow-sm'
                                        : 'bg-slate-50/90 text-slate-700 hover:bg-slate-100'
                                        }`}
                                >
                                    <span>{link.label}</span>
                                    <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                                </Link>
                            ))}
                        </div>
                        <div className="mt-4 grid gap-3 border-t border-slate-200/80 pt-4">
                            <Link
                                to="/signup"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="text-center rounded-2xl bg-navy py-3 text-sm font-bold text-white shadow-lg shadow-navy/10"
                            >
                                Teacher Sign Up
                            </Link>
                            <Link
                                to="/student-signup"
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="text-center rounded-2xl bg-primary py-3 text-sm font-bold text-white shadow-lg shadow-primary/20"
                            >
                                Student Sign Up
                            </Link>
                        </div>
                    </div>
                </div>
            </>
        )}
        </>
    );
}
